import { useState } from 'react';

import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { ExpandMore, InfoOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useAtomValue } from 'jotai';

import { selectedVisitAtom } from '../../../../app/appHeader/imageViewerMenu/atoms';
import { ToothIcon } from '../../../../common-components/elements/icon/svgs/ToothIcon';
import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import { usePreferences } from '../../../../react-query/usePreferences';
import { useSegmentations } from '../../../../react-query/useSegmentations';
import {
  black,
  blueBlack400,
  blueBlack650,
  orange700,
} from '../../../../theme/videa-style-tokens';
import { useUniversalToFDI } from '../../../../utils/internationalization/useUniversalToFDI';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../../../../utils/user';

import type { TDemoSegmentations } from './dmfDemo';
import { demoDmfData } from './dmfDemo';

const DMFEvaluationContainer = styled.div`
  color: white;
  .MuiAccordion-root {
    background: inherit;
    border-top: ${black} 1px solid;
  }
  svg {
    fill: white;
    width: 0.5em;
  }
  .MuiAccordionDetails-root {
    padding: 0 0.5em;
  }
  .MuiAccordionSummary-root {
    min-height: 0;
  }
  .MuiAccordionSummary-content {
    margin: 0.5em 0;
  }
  .MuiAccordionSummary-expandIconWrapper {
    width: 1em;
  }
  .MuiTypography-root {
    color: white;
  }
  .flex {
    display: flex;
    gap: 0.5em;
    justify-content: center;
    align-items: center;
  }
  .root-decay {
    color: ${orange700};
  }
  .partial-message {
  }

  .dmf-table,
  .dmf-body {
    border: none;
  }

  .dmf-icon {
    width: 3em;
  }

  .dmf-title {
    .MuiTypography-bodyExtrasmall {
      color: ${blueBlack400};
    }
  }

  .dmf-measure {
    .MuiTypography-bodyExtrasmall {
      font-weight: 700;
    }
    width: 3em;
  }
  .dmf-row {
    border-color: ${blueBlack650};

    &:last-child {
      border-color: transparent;

      .dmf-cell {
        border: none;
      }
    }
  }
  .dmf-cell {
    padding: 0.5em 0;
    border-color: ${blueBlack650};
  }
`;

export const DMFEvaluation = ({ imageId }: { imageId: string }) => {
  const preferencesQuery = usePreferences();
  const isDmfEnabled = Boolean(preferencesQuery?.data?.dmfEnabled);
  const isDmfRolloutEnabled = Boolean(
    preferencesQuery.data?.rolloutAverageDmfImageViewer,
  );
  const selectedVisitId = useAtomValue(selectedVisitAtom);
  const { data } = useSegmentations({
    estimatedVisitId: selectedVisitId ? parseInt(selectedVisitId) : undefined,
  });
  const isSalesDemo = !!useFeatureFlag(PracticeFeatureFlagKey.IS_SALES_DEMO)
    ?.value;

  const { displayToothNumber } = useUniversalToFDI();
  const { t } = useLingui();

  const teethDemoImage = demoDmfData.find(
    (image) => image.imageId === imageId,
  ) as TDemoSegmentations | undefined;

  const teeth =
    isSalesDemo && !!teethDemoImage
      ? teethDemoImage?.teeth
      : Array.isArray(data)
        ? data?.find((seg) => seg.imageId === imageId)?.teeth
        : [];

  const cleanTeeth = teeth
    ?.sort((a, b) =>
      a.toothId.localeCompare(b.toothId, undefined, { numeric: true }),
    )
    ?.map((tooth) => {
      let dmf: string | number | undefined = tooth.dmf;
      const decayOnCrown = tooth.decayOnCrown;
      if (typeof dmf === 'number') {
        dmf = dmf <= 0.05 ? '<5' : (dmf * 100).toFixed(0);
      } else {
        dmf = undefined;
      }
      return {
        ...tooth,
        dmf,
        decayOnCrown,
      };
    });
  const hasData = !!cleanTeeth?.some((tooth) => !!tooth?.dmf);
  const [isDmfOpen, setIsDmfOpen] = useState(true);

  if (!isDmfEnabled || !data || !hasData) {
    return null;
  }

  const handleDmfOpen = () => {
    setIsDmfOpen(!isDmfOpen);
  };

  return (
    <DMFEvaluationContainer>
      <Accordion
        square
        expanded={isDmfOpen}
        onClick={() => handleDmfOpen()}
        
        data-testid="DMF-header"
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <VideaTypography variant="bodySmall" className="flex">
            {isDmfRolloutEnabled ? t`Average DMF` : t`DMF Evaluation`}
            <Tooltip
              title={`${
                isDmfRolloutEnabled
                  ? t`Average value per tooth is calculated across all instances of decay in the image set.`
                  : ''
              }${t`DMF (Decayed, Missing, Filled) index is not a medical device and not cleared by the FDA.
              It is intended to be used for insurance documentation purposes.
              DMF only includes calculations directly from AI analysis (at default display threshold) 
              and does not include any changes made by user.`}`}
            >
              <InfoOutlined />
            </Tooltip>
          </VideaTypography>
        </AccordionSummary>
        <AccordionDetails>
          <VideaTypography>
            <Table className="dmf-table">
              <TableBody className="dmf-body">
                {cleanTeeth?.map(
                  (tooth) =>
                    tooth.dmf && (
                      <TableRow
                        className="dmf-row"
                        
                        data-testid="dmf-row"
                        key={tooth.toothId}
                      >
                        <TableCell className="dmf-cell">
                          <div className="flex dmf-icon">
                            <ToothIcon fill="white" />
                            <VideaTypography variant="bodyExtrasmall">
                              {displayToothNumber(tooth.toothId)}
                            </VideaTypography>
                          </div>
                        </TableCell>
                        <TableCell className="dmf-cell dmf-title">
                          <VideaTypography variant="bodyExtrasmall">
                            <Trans>Crown DMF</Trans>
                          </VideaTypography>
                        </TableCell>
                        <TableCell className="dmf-cell dmf-measure">
                          <VideaTypography variant="bodyExtrasmall">
                            {tooth.dmf}%
                          </VideaTypography>
                        </TableCell>
                        {/* {tooth.isPartial && (
                       <TableCell className="dmf-cell">
                         <VideaTypography
                           variant="bodyExtrasmall"
                           className="partial-message"
                         >
                           (partially hidden tooth)
                         </VideaTypography>
                       </TableCell>
                      )} */}
                        {/* <TableCell className="dmf-cell">
                       <VideaTypography
                         variant="bodyExtrasmall"
                         className="root-decay"
                       >
                         {tooth.caries?.some(
                           (c) =>
                             (c?.intoRootDentin &&
                               c?.intoRootDentin > 0.1) ||
                             (c?.rootInvolvement &&
                               c?.rootInvolvement !==
                                 CariesRootInvolvement.no_root_involvement),
                         )
                           ? 'Root Decay Present'
                           : ''}
                       </VideaTypography>
                      </TableCell> */}
                      </TableRow>
                    ),
                )}
              </TableBody>
            </Table>
          </VideaTypography>
        </AccordionDetails>
      </Accordion>
    </DMFEvaluationContainer>
  );
};
